import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, ArrowRight, Anchor, Wrench, Cog, Ship, CheckCircle } from "lucide-react";

const Projects = () => {
  const projects = [
    {
      id: 1,
      title: "Offshore Terminal Marine Hose Supply",
      category: "Marine Hose Systems",
      location: "Terengganu, Malaysia",
      year: "2024",
      description: "Supply of floating marine hose strings for offshore terminal operations in Terengganu including GMPHOM-compliant hoses and accessories.",
      image: "/images/offshore_terminal_1.png",
      icon: Anchor,
      color: "yellow",
      details: [
        "12-inch diameter floating marine hoses",
        "GMPHOM 2009 compliance",
        "Complete hose string assembly",
        "Installation support provided"
      ]
    },
    {
      id: 2,
      title: "Refinery Industrial Hose Delivery",
      category: "Industrial Hose",
      location: "Johor, Malaysia",
      year: "2024",
      description: "Delivery of flexible industrial hoses for refinery plant in Johor including chemical resistant and high-pressure hydraulic hoses.",
      image: "/images/industrial_hose_1.webp",
      icon: Wrench,
      color: "green",
      details: [
        "Chemical resistant hose assemblies",
        "High-pressure hydraulic hoses",
        "Custom flange configurations",
        "Fast delivery within 2 weeks"
      ]
    },
    {
      id: 3,
      title: "Pipeline Pig Launcher System Engineering",
      category: "Pig Launcher Systems",
      location: "Sabah, Malaysia",
      year: "2023",
      description: "Engineering & supply of pig launcher system for pipeline operator in Sabah including custom QOC and pigging accessories.",
      image: "/images/pig_launcher_1.webp",
      icon: Cog,
      color: "gray",
      details: [
        "24-inch pipeline diameter",
        "ASME standard construction",
        "Quick Opening Closure (QOC)",
        "Complete pigging accessories"
      ]
    },
    {
      id: 4,
      title: "OSV Charter for Offshore Construction",
      category: "Ship Brokering",
      location: "Sarawak, Malaysia",
      year: "2023",
      description: "Vessel charter arrangement for offshore construction support in Sarawak including OSV sourcing and marine logistics coordination.",
      image: "/images/ship_brokering_1.png",
      icon: Ship,
      color: "blue",
      details: [
        "Offshore Support Vessel charter",
        "3-month project duration",
        "Marine logistics coordination",
        "Documentation support"
      ]
    },
    {
      id: 5,
      title: "Bulk Cargo Tug & Barge Transport",
      category: "Marine Logistics",
      location: "Penang, Malaysia",
      year: "2023",
      description: "Tug & barge transport arrangement for bulk cargo movement in Penang including vessel sourcing and port coordination.",
      image: "/images/ship_brokering_2.jpeg",
      icon: Ship,
      color: "blue",
      details: [
        "5,000 MT cargo capacity",
        "Inter-port transport",
        "Port agency services",
        "Cargo handling coordination"
      ]
    },
    {
      id: 6,
      title: "Valve Supply for Petrochemical Plant",
      category: "Valve Products",
      location: "Kuala Lumpur, Malaysia",
      year: "2024",
      description: "Supply of API 6D compliant valves for petrochemical plant expansion including ball, gate, and butterfly valves.",
      image: "/images/petrochemical_valve_project_1.webp",
      icon: Cog,
      color: "purple",
      details: [
        "API 6D & ASME compliant valves",
        "Stainless steel construction",
        "High-pressure rated",
        "Complete documentation package"
      ]
    },
    {
      id: 7,
      title: "Process Skid Fabrication Project",
      category: "Process Skid Packages",
      location: "Selangor, Malaysia",
      year: "2024",
      description: "Design and fabrication of modular filtration and metering skids for oil terminal operations.",
      image: "/images/skid_manufacturing_1.jpeg",
      icon: Wrench,
      color: "indigo",
      details: [
        "Modular skid design",
        "Factory tested & certified",
        "ASME compliant fabrication",
        "Fast deployment capability"
      ]
    }
  ];

  const getColorClasses = (color: string) => {
    switch (color) {
      case "yellow":
        return {
          bg: "bg-yellow-100",
          text: "text-yellow-600",
          badge: "bg-yellow-500"
        };
      case "green":
        return {
          bg: "bg-green-100",
          text: "text-green-600",
          badge: "bg-green-500"
        };
      case "gray":
        return {
          bg: "bg-gray-100",
          text: "text-gray-600",
          badge: "bg-gray-500"
        };
      case "blue":
        return {
          bg: "bg-blue-100",
          text: "text-blue-600",
          badge: "bg-blue-500"
        };
      case "purple":
        return {
          bg: "bg-purple-100",
          text: "text-purple-600",
          badge: "bg-purple-500"
        };
      case "indigo":
        return {
          bg: "bg-indigo-100",
          text: "text-indigo-600",
          badge: "bg-indigo-500"
        };
      default:
        return {
          bg: "bg-gray-100",
          text: "text-gray-600",
          badge: "bg-gray-500"
        };
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6">Our Projects</h1>
            <p className="text-xl text-blue-100">
              Showcasing successful marine and industrial engineering solutions across Southeast Asia
            </p>
          </div>
        </div>
      </section>

      {/* Project Stats */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 max-w-4xl mx-auto text-center">
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">100+</div>
              <div className="text-gray-700">Projects Completed</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-500 mb-2">6</div>
              <div className="text-gray-700">Countries Served</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600 mb-2">15+</div>
              <div className="text-gray-700">Years Experience</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600 mb-2">50+</div>
              <div className="text-gray-700">Satisfied Clients</div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlighted Projects */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Highlighted Projects</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Recent successful implementations of our marine and industrial engineering solutions
            </p>
          </div>
          
          <div className="space-y-8 max-w-6xl mx-auto">
            {projects.map((project, index) => {
              const colors = getColorClasses(project.color);
              const IconComponent = project.icon;
              
              return (
                <Card key={project.id} className={`shadow-lg border-0 overflow-hidden ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={`lg:flex ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                    <div className="lg:w-1/2">
                      <img 
                        src={project.image} 
                        alt={project.title}
                        className="w-full h-64 lg:h-full object-cover"
                      />
                    </div>
                    <div className="lg:w-1/2 p-8">
                      <CardHeader className="p-0 mb-6">
                        <div className="flex items-center space-x-4 mb-4">
                          <div className={`w-12 h-12 ${colors.bg} rounded-full flex items-center justify-center`}>
                            <IconComponent className={`h-6 w-6 ${colors.text}`} />
                          </div>
                          <Badge className={`${colors.badge} text-white`}>
                            {project.category}
                          </Badge>
                        </div>
                        <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                          {project.title}
                        </CardTitle>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <div className="flex items-center space-x-1">
                            <MapPin className="h-4 w-4" />
                            <span>{project.location}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-4 w-4" />
                            <span>{project.year}</span>
                          </div>
                        </div>
                      </CardHeader>
                      
                      <CardContent className="p-0">
                        <CardDescription className="text-gray-700 mb-6 text-base leading-relaxed">
                          {project.description}
                        </CardDescription>
                        
                        <div className="mb-6">
                          <h4 className="font-semibold text-gray-900 mb-3">Project Highlights:</h4>
                          <ul className="space-y-2">
                            {project.details.map((detail, detailIndex) => (
                              <li key={detailIndex} className="flex items-start space-x-2">
                                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                <span className="text-sm text-gray-700">{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        <Button variant="outline" className="group">
                          View Project Details
                          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </CardContent>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Project Categories */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Project Categories</h2>
            <p className="text-xl text-gray-600">Our expertise spans across multiple marine and industrial sectors</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-6xl mx-auto">
            {[
              {
                icon: Anchor,
                title: "Marine Hose Systems",
                count: "40+",
                description: "Offshore terminals, FPSO operations, marine loading",
                color: "yellow"
              },
              {
                icon: Wrench,
                title: "Industrial Hose Supply",
                count: "35+",
                description: "Refineries, petrochemical plants, manufacturing",
                color: "green"
              },
              {
                icon: Cog,
                title: "Pig Launcher Systems",
                count: "15+",
                description: "Pipeline maintenance, commissioning, cleaning",
                color: "gray"
              },
              {
                icon: Cog,
                title: "Valve Products",
                count: "20+",
                description: "API 6D valves, ball, gate, butterfly valves",
                color: "purple"
              },
              {
                icon: Wrench,
                title: "Process Skid Packages",
                count: "12+",
                description: "Modular systems, metering, filtration skids",
                color: "indigo"
              },
              {
                icon: Ship,
                title: "Marine Services",
                count: "25+",
                description: "Vessel charter, logistics, marine agency",
                color: "blue"
              }
            ].map((category, index) => {
              const colors = getColorClasses(category.color);
              const IconComponent = category.icon;
              
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow border-0 shadow-md">
                  <CardHeader>
                    <div className={`w-16 h-16 ${colors.bg} rounded-full flex items-center justify-center mx-auto mb-4`}>
                      <IconComponent className={`h-8 w-8 ${colors.text}`} />
                    </div>
                    <CardTitle className="text-xl">{category.title}</CardTitle>
                    <div className="text-2xl font-bold text-gray-900">{category.count}</div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>{category.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Next Project?</h2>
          <p className="text-xl text-blue-100 mb-8">
            Let our experienced engineering team help you with your marine and industrial requirements
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4">
              Discuss Your Project
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4">
              Request Quotation
            </Button>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Projects;